import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const EMPTY = { name: '', subject: '', body: '' };

export default function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [editor, setEditor] = useState(null);

  const load = () => api.get('/admin/reply-templates').then(r => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editor.name || !editor.body) { toast.error('Name and body required'); return; }
    try {
      if (editor.id) await api.put(`/admin/reply-templates/${editor.id}`, editor);
      else await api.post('/admin/reply-templates', editor);
      toast.success('Template saved');
      setEditor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await api.delete(`/admin/reply-templates/${id}`);
    toast.success('Deleted');
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-serif">Reply Templates</h2>
          <p className="text-sm text-[#3D2817]/60">Reusable messages for quick replies. Variables: {'{{contact_name}}, {{company}}, {{country}}, {{product_list}}'}</p>
        </div>
        <Button data-testid="new-template" onClick={() => setEditor({ ...EMPTY })}>
          <Plus className="w-4 h-4 mr-2" /> New Template
        </Button>
      </div>
      <div className="border rounded-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Preview</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-[#3D2817]/50">No templates yet.</TableCell></TableRow>
            )}
            {templates.map(t => (
              <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                <TableCell className="font-serif">{t.name}</TableCell>
                <TableCell className="text-sm text-[#3D2817]/70">{t.subject || '—'}</TableCell>
                <TableCell className="text-xs text-[#3D2817]/60 max-w-md truncate">{t.body?.substring(0, 90)}{t.body?.length > 90 ? '…' : ''}</TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`template-edit-${t.id}`} variant="ghost" size="icon" onClick={() => setEditor({ ...t })}><Pencil className="w-4 h-4" /></Button>
                  <Button data-testid={`template-delete-${t.id}`} variant="ghost" size="icon" onClick={() => remove(t.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editor?.id ? 'Edit Template' : 'New Template'}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-4 py-2">
              <div>
                <Label>Name *</Label>
                <Input data-testid="template-name" value={editor.name} onChange={e => setEditor({ ...editor, name: e.target.value })} placeholder="e.g. Discount Offer" />
              </div>
              <div>
                <Label>Email subject</Label>
                <Input value={editor.subject} onChange={e => setEditor({ ...editor, subject: e.target.value })} placeholder="Re: Your inquiry about {{company}}" />
              </div>
              <div>
                <Label>Body *</Label>
                <Textarea data-testid="template-body" rows={10} value={editor.body} onChange={e => setEditor({ ...editor, body: e.target.value })}
                  placeholder="Hello {{contact_name}},&#10;&#10;Thank you for reaching out..." />
                <div className="text-xs text-[#3D2817]/60 mt-2">
                  Variables: <code className="bg-[#F2ECE0] px-1">{'{{contact_name}}'}</code> <code className="bg-[#F2ECE0] px-1">{'{{company}}'}</code> <code className="bg-[#F2ECE0] px-1">{'{{country}}'}</code> <code className="bg-[#F2ECE0] px-1">{'{{product_list}}'}</code>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)}>Cancel</Button>
            <Button data-testid="template-save" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
